import {Box} from 'exsportia-components'
import {useFormikContext} from 'formik'
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useLocation, useNavigate} from 'react-router-dom'

import {fetchFormForEntity} from '../../api/forms'
import {BookingFormData} from '../../api/forms/types'
import {useSubscription} from '../../api/subscriptions/hooks'
import {BookingFormModalContent} from '../../components/booking-form-step/booking-form-modal-content'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {useBookingLoader} from '../../services/booking/hooks'
import {GradientLoader} from '../../ui/components/gradient-loader/gradient-loader'
import {useLocale} from '../../utils/i18n'
import {BookingCommonType} from '../booking-summary/user-info-section'
import {FORM_STALE_TIME, FormStepType, FormStepValues, getFormEntityIds, getFormEntityType, getFormForEntityQueryKey} from './form-step'

type Props = {
  type: FormStepType
  nextStepPath: string
}

const Header: FC<{ title?: string; onBack?: () => void }> = ({title, onBack}) => {
  const {l} = useLocale()
  return <SimpleGridLayoutHeader title={title || l('form', {defaultValue: 'Form'})} customBackAction={onBack} />
}

export const BookingFormPage: FC<Props> = ({type, nextStepPath}) => {
  const navigate = useNavigate()
  const {values, setFieldValue, submitForm} = useFormikContext<BookingCommonType & { formData?: BookingFormData; formsData?: Record<string, BookingFormData> }>()
  const {data: subscription, isLoading: isSubscriptionLoading} = useSubscription()
  const location = useLocation()
  const actualNextStepPath = (location.state as any)?.nextStepPath || nextStepPath
  const {isLoading: isBookingLoading} = useBookingLoader()

  const entityIds = useMemo(
    () => getFormEntityIds(type, values as FormStepValues, subscription?.uid),
    [type, values, subscription],
  )

  const [currentEntityIndex, setCurrentEntityIndex] = useState(0)
  const currentEntityId = entityIds[currentEntityIndex]
  const entityType = getFormEntityType(type, values as FormStepValues)

  const currentClassName = useMemo(() => {
    if (type !== 'class') {
      return ''
    }
    const val = values as any
    if (val.selectedSessions) {
      const session = val.selectedSessions.find((s: any) => (s.classId || val.class?.uid || val.classId) === currentEntityId)
      if (session?.className) {
        return session.className
      }
    }
    if (val.class?.uid === currentEntityId) {
      return val.class.name
    }
    return ''
  }, [type, values, currentEntityId])

  // `replace` is for the automatic skip of a step with no form: the step must not stay in the
  // history, or Back from Summary lands on it and its mount-time skip bounces the customer
  // straight back to Summary (WBA-4708). A step the customer actually filled in is pushed, so
  // Back returns to it.
  const handleNextStep = useCallback((options?: {replace?: boolean}) => {
    if (actualNextStepPath === 'submit') {
      submitForm().catch(console.error)
    } else {
      navigate(actualNextStepPath, {replace: options?.replace === true})
    }
  }, [actualNextStepPath, submitForm, navigate])

  const handleBack = useCallback(() => {
    if (currentEntityIndex > 0) {
      setCurrentEntityIndex(prev => prev - 1)
    } else {
      navigate(-1)
    }
  }, [currentEntityIndex, navigate])

  const proceedToNextEntity = useCallback((options?: {replace?: boolean}) => {
    if (currentEntityIndex + 1 < entityIds.length) {
      setCurrentEntityIndex(prev => prev + 1)
    } else {
      handleNextStep(options)
    }
  }, [currentEntityIndex, entityIds.length, handleNextStep])

  const shouldFetchForm = Boolean(currentEntityId && !(type === 'subscription' && isSubscriptionLoading))

  const { data: form, isError, error, isSuccess } = useQuery(
    getFormForEntityQueryKey(entityType, currentEntityId),
    () => fetchFormForEntity(entityType, currentEntityId),
    {
      enabled: shouldFetchForm,
      cacheTime: 0,
      staleTime: FORM_STALE_TIME,
      retry: false,
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    if (type === 'subscription' && isSubscriptionLoading) {
      return
    }

    if (!currentEntityId) {
      proceedToNextEntity({replace: true})
      return
    }

    if (isError) {
      console.error('Failed to fetch form:', error)
      proceedToNextEntity({replace: true})
      return
    }

    if (isSuccess && !form) {
      proceedToNextEntity({replace: true})
    }
  }, [type, isSubscriptionLoading, currentEntityId, isError, error, isSuccess, form, proceedToNextEntity])

  const handleComplete = useCallback(
    (data: any) => {
      const currentFormsData = values.formsData || {}

      Promise.all([
        setFieldValue('formsData', {
          ...currentFormsData,
          [currentEntityId]: data
        }, true),
        // For backwards compatibility or single entity flows
        setFieldValue('formData', data, true)
      ]).then(() => {
        proceedToNextEntity()
      })
    },
    [setFieldValue, values.formsData, currentEntityId, proceedToNextEntity],
  )

  // The previous step already looked the form up, so this skeleton normally shows for a moment at
  // most. A form that is being refetched in the background stays on screen.
  if (!form) {
    return (
      <GridLayout header={<Header onBack={handleBack} />}>
        <Box p='16px'>
          <GradientLoader style={{width: '60%'}} />
          <Box mt='16px'>
            <GradientLoader height='48px' />
          </Box>
          <Box mt='16px'>
            <GradientLoader height='48px' />
          </Box>
        </Box>
      </GridLayout>
    )
  }

  const title = form.title
  const displayTitle = currentClassName ? `${currentClassName} - ${title}` : title

  return (
    <GridLayout header={<Header title={displayTitle} onBack={handleBack} />}>
      <Box p='16px'>
        <BookingFormModalContent
          key={currentEntityId}
          form={form}
          initialData={values.formsData?.[currentEntityId]}
          isLoading={isBookingLoading}
          onComplete={handleComplete}
        />
      </Box>
    </GridLayout>
  )
}
